import { useState } from "react"
import { useLocation, useNavigate } from "react-router-dom"

import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Chip,
  Divider,
  FormControlLabel,
  IconButton,
  InputAdornment,
  Stack,
  TextField,
  Typography,
} from "@mui/material"

import {
  EmailOutlined,
  LockOutlined,
  Visibility,
  VisibilityOff,
} from "@mui/icons-material"

import loginBg from "../../assets/login-bg.jpeg"
import { appConfig } from "../../config/appConfig"

// ======================================================
// CONFIGURATION APPLICATION
// ======================================================

const APP_NAME =
  appConfig.displayName ||
  appConfig.appName ||
  "RLSys Business"

const APP_SHORT_NAME =
  appConfig.shortName ||
  "RLSys"

const APP_MEANING =
  appConfig.fullMeaning ||
  "Retail & Logistics System"

const APP_VERSION =
  appConfig.version ||
  "v1.0.0"

const APP_LOGO =
  appConfig.logo ||
  "/rlsys.png"

const APP_SLOGAN =
  appConfig.loginSlogan ||
  "ERP All-in-One • Automatisation des processus métier"

const APP_COPYRIGHT =
  appConfig.copyrightLabel ||
  `© 2026 ${APP_NAME} ERP. Tous droits réservés.`

const APP_FOOTER_NOTE =
  appConfig.footerNote ||
  ""

// Support RLSys
const SUPPORT_EMAIL =
  appConfig.supportEmail ||
  "rlsys.business@gmail.com"

const SUPPORT_EMAIL_SUBJECT =
  encodeURIComponent("RLSys Business - Assistance connexion")

const SUPPORT_MAILTO =
  `mailto:${SUPPORT_EMAIL}?subject=${SUPPORT_EMAIL_SUBJECT}`

// ======================================================
// CONFIGURATION API
// ======================================================

const RAW_API_BASE_URL =
  import.meta.env.VITE_API_BASE_URL ||
  "http://127.0.0.1:8000"

const CLEAN_API_BASE_URL =
  RAW_API_BASE_URL.replace(/\/$/, "")

const API_URL =
  CLEAN_API_BASE_URL.endsWith("/api")
    ? CLEAN_API_BASE_URL
    : `${CLEAN_API_BASE_URL}/api`

// ======================================================
// REDIRECTIONS SELON LES RÔLES
// ======================================================

const ROLE_REDIRECTS = {
  platform_admin: "/dashboard-pro",
  super_admin: "/dashboard-pro",
  dg: "/dashboard-pro",

  admin_maison_djo: "/maison-djo-dashboard",
  gestionnaire_maison_djo: "/maison-djo-dashboard",
  stock_maison_djo: "/stock-orders",
  livreur_maison_djo: "/stock-orders",

  admin_depot_relais: "/depot-relais-dashboard",
  gestionnaire_depot_relais: "/depot-relais-dashboard",
  stock_depot_relais: "/stock-orders",

  gerant_depot_relais: "/sales",
  gerant_magasin: "/sales",

  agent_commercial: "/customers",

  gestionnaire_operations: "/reporting",
  gestionnaire_stock: "/stock-orders",
  caissier_central: "/payments",
  controleur_stock: "/stock-audits",

  viewer: "/reporting",
}

// ======================================================
// INFORMATIONS TECHNIQUES DU RUNTIME
// Utilisées uniquement dans la console développeur.
// Elles ne sont jamais affichées au client.
// ======================================================

function getRuntimeInfo() {
  const protocol =
    window.location.protocol || ""

  const hostname =
    window.location.hostname || ""

  const href =
    window.location.href || ""

  const isCapacitor =
    protocol === "capacitor:" ||
    protocol === "file:" ||
    hostname === "localhost" ||
    hostname === "127.0.0.1"

  return {
    protocol,
    hostname,
    href,
    isCapacitor,
    apiUrl: API_URL,
    loginUrl: `${API_URL}/auth/login`,
  }
}

// ======================================================
// REDIRECTION
// ======================================================

function getRedirectPath(role) {
  return ROLE_REDIRECTS[role] || "/dashboard-pro"
}

// ======================================================
// EXTRACTION MESSAGE BACKEND
// Usage interne uniquement.
// ======================================================

function getErrorDetailFromData(data) {
  if (!data) {
    return ""
  }

  if (typeof data === "string") {
    return data
  }

  if (typeof data.detail === "string") {
    return data.detail
  }

  if (Array.isArray(data.detail)) {
    return data.detail
      .map((item) => {
        if (typeof item === "string") {
          return item
        }

        if (item?.msg) {
          return item.msg
        }

        try {
          return JSON.stringify(item)
        } catch {
          return ""
        }
      })
      .filter(Boolean)
      .join("\n")
  }

  if (typeof data.message === "string") {
    return data.message
  }

  if (typeof data.error === "string") {
    return data.error
  }

  try {
    return JSON.stringify(data)
  } catch {
    return ""
  }
}

// ======================================================
// CONNEXION API
// ======================================================

async function loginWithFetch(email, password) {
  const loginUrl =
    `${API_URL}/auth/login`

  const controller =
    new AbortController()

  const timeoutId =
    window.setTimeout(() => {
      controller.abort()
    }, 45000)

  try {
    console.log("[RLSYS_LOGIN_START]", {
      loginUrl,
      email,
      runtime: getRuntimeInfo(),
    })

    const response =
      await fetch(loginUrl, {
        method: "POST",
        mode: "cors",
        cache: "no-store",
        credentials: "omit",

        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
        },

        body: JSON.stringify({
          email,
          password,
        }),

        signal: controller.signal,
      })

    const rawText =
      await response.text()

    let data = null

    if (rawText) {
      try {
        data = JSON.parse(rawText)
      } catch {
        data = {
          detail: rawText,
        }
      }
    }

    console.log("[RLSYS_LOGIN_RESPONSE]", {
      status: response.status,
      ok: response.ok,
      data,
    })

    if (!response.ok) {
      const detail =
        getErrorDetailFromData(data)

      const error =
        new Error(
          detail ||
            `Connexion refusée par le serveur. Code ${response.status}.`
        )

      error.status = response.status
      error.data = data
      error.rawText = rawText
      error.loginUrl = loginUrl

      throw error
    }

    return data
  } catch (error) {
    console.error(
      "[RLSYS_LOGIN_ERROR]",
      {
        error,
        loginUrl,
        runtime: getRuntimeInfo(),
      }
    )

    // Timeout
    if (error?.name === "AbortError") {
      const timeoutError =
        new Error(
          "Le serveur met trop de temps à répondre."
        )

      timeoutError.status = "TIMEOUT"
      timeoutError.loginUrl = loginUrl

      throw timeoutError
    }

    // Erreur réseau
    if (
      error?.message === "Failed to fetch" ||
      error?.message ===
        "NetworkError when attempting to fetch resource."
    ) {
      const networkError =
        new Error(
          "Impossible de joindre le serveur."
        )

      networkError.status = "NETWORK"
      networkError.loginUrl = loginUrl
      networkError.originalMessage =
        error.message

      throw networkError
    }

    throw error
  } finally {
    window.clearTimeout(timeoutId)
  }
}

// ======================================================
// PAGE LOGIN
// ======================================================

function LoginPage() {
  const navigate = useNavigate()
  const location = useLocation()

  const [email, setEmail] =
    useState("")

  const [password, setPassword] =
    useState("")

  const [showPassword, setShowPassword] =
    useState(false)

  const [rememberMe, setRememberMe] =
    useState(true)

  const [error, setError] =
    useState("")

  const [loading, setLoading] =
    useState(false)

  // ====================================================
  // SUPPRESSION ANCIENNE SESSION
  // ====================================================

  const clearOldSession = () => {
    const keys = [
      "kongosys_user",
      "kongosys_user_id",
      "kongosys_full_name",
      "kongosys_role",
      "kongosys_role_id",
      "kongosys_company_id",

      "kongosys_organization_id",
      "kongosys_organization_code",
      "kongosys_organization_name",
      "kongosys_organization_type",

      "kongosys_store_id",
      "kongosys_store_code",
      "kongosys_store_name",

      "kongosys_warehouse_id",
      "kongosys_warehouse_code",
      "kongosys_warehouse_name",

      "kongosys_remember",
    ]

    keys.forEach((key) => {
      localStorage.removeItem(key)
    })
  }

  // ====================================================
  // ENREGISTREMENT SESSION UTILISATEUR
  // ====================================================

  const saveUserSession = (user) => {
    clearOldSession()

    const role =
      user.role ||
      user.role_name ||
      ""

    localStorage.setItem(
      "kongosys_user",
      user.email || ""
    )

    localStorage.setItem(
      "kongosys_user_id",
      String(user.id || "")
    )

    localStorage.setItem(
      "kongosys_full_name",
      user.full_name || "Utilisateur"
    )

    localStorage.setItem(
      "kongosys_role",
      role
    )

    localStorage.setItem(
      "kongosys_role_id",
      String(user.role_id || "")
    )

    localStorage.setItem(
      "kongosys_company_id",
      String(user.company_id || "1")
    )

    localStorage.setItem(
      "kongosys_remember",
      rememberMe ? "true" : "false"
    )

    // Organisation
    if (
      user.organization_id !== null &&
      user.organization_id !== undefined
    ) {
      localStorage.setItem(
        "kongosys_organization_id",
        String(user.organization_id)
      )
    }

    if (user.organization_code) {
      localStorage.setItem(
        "kongosys_organization_code",
        user.organization_code
      )
    }

    if (user.organization_name) {
      localStorage.setItem(
        "kongosys_organization_name",
        user.organization_name
      )
    }

    if (user.organization_type) {
      localStorage.setItem(
        "kongosys_organization_type",
        user.organization_type
      )
    }

    // Shop
    if (
      user.store_id !== null &&
      user.store_id !== undefined
    ) {
      localStorage.setItem(
        "kongosys_store_id",
        String(user.store_id)
      )
    }

    if (user.store_code) {
      localStorage.setItem(
        "kongosys_store_code",
        user.store_code
      )
    }

    if (user.store_name) {
      localStorage.setItem(
        "kongosys_store_name",
        user.store_name
      )
    }

    // Entrepôt
    if (
      user.warehouse_id !== null &&
      user.warehouse_id !== undefined
    ) {
      localStorage.setItem(
        "kongosys_warehouse_id",
        String(user.warehouse_id)
      )
    }

    if (user.warehouse_code) {
      localStorage.setItem(
        "kongosys_warehouse_code",
        user.warehouse_code
      )
    }

    if (user.warehouse_name) {
      localStorage.setItem(
        "kongosys_warehouse_name",
        user.warehouse_name
      )
    }

    return role
  }

  // ====================================================
  // LOGIN
  // ====================================================

  const handleLogin = async (event) => {
    event.preventDefault()

    setError("")

    const cleanEmail =
      String(email || "")
        .trim()
        .toLowerCase()

    const cleanPassword =
      String(password || "")
        .trim()

    // Champs obligatoires
    if (!cleanEmail || !cleanPassword) {
      setError(
        "Veuillez saisir votre adresse e-mail et votre mot de passe."
      )
      return
    }

    try {
      setLoading(true)

      const data =
        await loginWithFetch(
          cleanEmail,
          cleanPassword
        )

      const user =
        data?.user

      // Réponse inattendue du backend
      if (!user) {
        console.error(
          "[RLSYS_INVALID_LOGIN_RESPONSE]",
          data
        )

        setError(
          `Le service de connexion a rencontré un problème. Veuillez réessayer. Si le problème persiste, contactez ${SUPPORT_EMAIL}.`
        )

        return
      }

      const role =
        saveUserSession(user)

      const fromPath =
        location.state?.from?.pathname

      const redirectPath =
        fromPath &&
        fromPath !== "/login"
          ? fromPath
          : getRedirectPath(role)

      navigate(
        redirectPath,
        {
          replace: true,
        }
      )
    } catch (loginError) {
      console.error(
        "[RLSYS_LOGIN_FAILED]",
        {
          status: loginError?.status,
          message: loginError?.message,
          data: loginError?.data,
          runtime: getRuntimeInfo(),
        }
      )

      const status =
        loginError?.status

      // ----------------------------------------------
      // IDENTIFIANTS INCORRECTS
      // ----------------------------------------------

      if (
        status === 401 ||
        status === 403
      ) {
        setError(
          `Adresse e-mail ou mot de passe incorrect. Vérifiez vos identifiants. Si vous avez oublié votre mot de passe, écrivez à ${SUPPORT_EMAIL}.`
        )
      }

      // ----------------------------------------------
      // DONNÉES INVALIDES
      // ----------------------------------------------

      else if (status === 422) {
        setError(
          "Les informations de connexion sont invalides. Vérifiez votre adresse e-mail et votre mot de passe."
        )
      }

      // ----------------------------------------------
      // SERVEUR
      // ----------------------------------------------

      else if (
        typeof status === "number" &&
        status >= 500
      ) {
        setError(
          `Le service de connexion rencontre momentanément un problème. Veuillez réessayer dans quelques instants. Si le problème persiste, contactez ${SUPPORT_EMAIL}.`
        )
      }

      // ----------------------------------------------
      // ERREUR RÉSEAU
      // ----------------------------------------------

      else if (status === "NETWORK") {
        setError(
          `Le service de connexion est momentanément indisponible. Veuillez réessayer dans quelques instants. Si le problème persiste, contactez ${SUPPORT_EMAIL}.`
        )
      }

      // ----------------------------------------------
      // TIMEOUT
      // ----------------------------------------------

      else if (status === "TIMEOUT") {
        setError(
          `Le service met trop de temps à répondre. Veuillez réessayer. Si le problème persiste, contactez ${SUPPORT_EMAIL}.`
        )
      }

      // ----------------------------------------------
      // AUTRES ERREURS
      // ----------------------------------------------

      else {
        setError(
          `Connexion impossible pour le moment. Veuillez réessayer ou contactez ${SUPPORT_EMAIL}.`
        )
      }
    } finally {
      setLoading(false)
    }
  }

  // ======================================================
  // INTERFACE
  // ======================================================


  return (
    <Box
      sx={{
        minHeight: "100dvh",
        position: "relative",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        px: { xs: 1.5, sm: 2.5 },
        py: { xs: 7.5, sm: 5 },
        backgroundImage: `url(${loginBg})`,
        backgroundSize: "cover",
        backgroundPosition: "center",
        backgroundAttachment: { xs: "scroll", md: "fixed" },
        overflow: "hidden",
      }}
    >
      {/* Fond / overlay */}
      <Box
        sx={{
          position: "absolute",
          inset: 0,
          background:
            "radial-gradient(circle at 82% 18%, rgba(59,130,246,0.28) 0%, rgba(59,130,246,0) 32%), linear-gradient(135deg, rgba(2,6,23,0.96) 0%, rgba(9,22,52,0.90) 48%, rgba(29,78,216,0.72) 100%)",
        }}
      />

      <Box
        sx={{
          position: "absolute",
          width: { xs: 260, md: 460 },
          height: { xs: 260, md: 460 },
          top: { xs: -150, md: -240 },
          right: { xs: -130, md: -180 },
          borderRadius: "50%",
          border: { xs: "46px solid rgba(255,255,255,0.018)", md: "76px solid rgba(255,255,255,0.02)" },
          pointerEvents: "none",
        }}
      />

      <Box
        sx={{
          position: "absolute",
          width: { xs: 220, md: 360 },
          height: { xs: 220, md: 360 },
          bottom: { xs: -150, md: -220 },
          left: { xs: -120, md: -150 },
          borderRadius: "50%",
          background: "rgba(37,99,235,0.10)",
          filter: "blur(3px)",
          pointerEvents: "none",
        }}
      />

      {/* Retour au site */}
      <Button
        type="button"
        onClick={() => navigate("/")}
        variant="outlined"
        sx={{
          position: "absolute",
          zIndex: 4,
          top: { xs: 14, sm: 20 },
          right: { xs: 14, sm: 22 },
          minHeight: 42,
          px: { xs: 1.6, sm: 2.2 },
          borderRadius: 999,
          color: "#ffffff",
          borderColor: "rgba(255,255,255,0.34)",
          background: "rgba(15,23,42,0.28)",
          backdropFilter: "blur(12px)",
          WebkitBackdropFilter: "blur(12px)",
          textTransform: "none",
          fontWeight: 850,
          fontSize: { xs: "0.78rem", sm: "0.86rem" },
          boxShadow: "0 8px 24px rgba(2,6,23,0.12)",
          "&:hover": {
            color: "#ffffff",
            borderColor: "rgba(255,255,255,0.62)",
            background: "rgba(255,255,255,0.12)",
          },
        }}
      >
        ← Retour au site
      </Button>

      {/* Carte de connexion centrale — structure conservée */}
      <Card
        elevation={0}
        sx={{
          position: "relative",
          zIndex: 2,
          width: "100%",
          maxWidth: { xs: 430, sm: 475 },
          borderRadius: { xs: 4, sm: 4.5 },
          overflow: "hidden",
          backgroundColor: "rgba(255,255,255,0.985)",
          border: "1px solid rgba(255,255,255,0.72)",
          boxShadow: "0 34px 90px rgba(2,6,23,0.44)",
          backdropFilter: "blur(16px)",
          WebkitBackdropFilter: "blur(16px)",
          "&::before": {
            content: '""',
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            height: 5,
            background: "linear-gradient(90deg, #0f172a 0%, #1d4ed8 50%, #4f46e5 100%)",
          },
        }}
      >
        <CardContent
          sx={{
            p: { xs: 2.4, sm: 3.8 },
            "&:last-child": { pb: { xs: 2.4, sm: 3.6 } },
          }}
        >
          {/* Identité RLSys */}
          <Box sx={{ textAlign: "center", mb: { xs: 2.1, sm: 2.5 } }}>
            <Box
              sx={{
                width: { xs: 72, sm: 80 },
                height: { xs: 72, sm: 80 },
                mx: "auto",
                mb: 1.35,
                p: 0.75,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                borderRadius: 3,
                bgcolor: "#ffffff",
                border: "1px solid rgba(15,23,42,0.08)",
                boxShadow: "0 14px 34px rgba(15,23,42,0.13)",
              }}
            >
              <Box
                component="img"
                src={APP_LOGO}
                alt={`Logo ${APP_SHORT_NAME}`}
                onError={(event) => {
                  event.currentTarget.style.display = "none"
                }}
                sx={{
                  width: "100%",
                  height: "100%",
                  objectFit: "contain",
                  display: "block",
                }}
              />
            </Box>

            <Typography
              component="h1"
              sx={{
                color: "#0b1327",
                fontSize: { xs: "1.8rem", sm: "2.05rem" },
                lineHeight: 1.08,
                letterSpacing: "-0.035em",
                fontWeight: 950,
              }}
            >
              {APP_NAME}
            </Typography>

            <Typography
              sx={{
                mt: 0.55,
                color: "#475569",
                fontSize: { xs: "0.78rem", sm: "0.82rem" },
                fontWeight: 800,
              }}
            >
              {APP_MEANING} • {APP_VERSION}
            </Typography>

            <Chip
              label={APP_SLOGAN}
              size="small"
              sx={{
                mt: 1.25,
                height: "auto",
                maxWidth: "100%",
                borderRadius: 999,
                color: "#1e40af",
                bgcolor: "#eff6ff",
                border: "1px solid rgba(37,99,235,0.12)",
                fontWeight: 850,
                "& .MuiChip-label": {
                  px: 1.4,
                  py: 0.55,
                  whiteSpace: "normal",
                  textAlign: "center",
                  lineHeight: 1.3,
                  fontSize: "0.72rem",
                },
              }}
            />

            <Typography
              sx={{
                mt: 1.15,
                mx: "auto",
                maxWidth: 370,
                color: "#64748b",
                fontSize: { xs: "0.78rem", sm: "0.82rem" },
                lineHeight: 1.6,
              }}
            >
              Connectez-vous pour accéder à votre espace opérationnel et aux modules autorisés de votre organisation.
            </Typography>
          </Box>

          {error ? (
            <Alert
              severity="error"
              variant="outlined"
              sx={{
                mb: 1.8,
                borderRadius: 2.5,
                bgcolor: "rgba(254,242,242,0.72)",
                alignItems: "flex-start",
                "& .MuiAlert-message": {
                  width: "100%",
                  lineHeight: 1.5,
                  fontSize: "0.82rem",
                },
              }}
            >
              {error}
            </Alert>
          ) : null}

          <Box component="form" onSubmit={handleLogin}>
            <Stack spacing={1.45}>
              <TextField
                label="Adresse e-mail"
                type="email"
                fullWidth
                value={email}
                onChange={(event) => setEmail(event.target.value)}
                disabled={loading}
                autoComplete="email"
                placeholder="nom@entreprise.com"
                sx={{
                  "& .MuiOutlinedInput-root": {
                    minHeight: 52,
                    borderRadius: 2.4,
                    bgcolor: "#f8fafc",
                    transition: "all 0.18s ease",
                    "&:hover": { bgcolor: "#ffffff" },
                    "&.Mui-focused": {
                      bgcolor: "#ffffff",
                      boxShadow: "0 0 0 4px rgba(37,99,235,0.08)",
                    },
                  },
                  "& .MuiInputLabel-root": { fontWeight: 700 },
                }}
                slotProps={{
                  input: {
                    startAdornment: (
                      <InputAdornment position="start">
                        <EmailOutlined sx={{ color: "#64748b", fontSize: 21 }} />
                      </InputAdornment>
                    ),
                  },
                }}
              />

              <TextField
                label="Mot de passe"
                type={showPassword ? "text" : "password"}
                fullWidth
                value={password}
                onChange={(event) => setPassword(event.target.value)}
                disabled={loading}
                autoComplete="current-password"
                sx={{
                  "& .MuiOutlinedInput-root": {
                    minHeight: 52,
                    borderRadius: 2.4,
                    bgcolor: "#f8fafc",
                    transition: "all 0.18s ease",
                    "&:hover": { bgcolor: "#ffffff" },
                    "&.Mui-focused": {
                      bgcolor: "#ffffff",
                      boxShadow: "0 0 0 4px rgba(37,99,235,0.08)",
                    },
                  },
                  "& .MuiInputLabel-root": { fontWeight: 700 },
                }}
                slotProps={{
                  input: {
                    startAdornment: (
                      <InputAdornment position="start">
                        <LockOutlined sx={{ color: "#64748b", fontSize: 21 }} />
                      </InputAdornment>
                    ),
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          edge="end"
                          onClick={() => setShowPassword((previous) => !previous)}
                          disabled={loading}
                          aria-label={showPassword ? "Masquer le mot de passe" : "Afficher le mot de passe"}
                          sx={{ color: "#64748b" }}
                        >
                          {showPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  },
                }}
              />
            </Stack>

            <Stack
              direction="row"
              useFlexGap
              flexWrap="wrap"
              gap={0.8}
              sx={{
                mt: 1,
                mb: 2.1,
                alignItems: "center",
                justifyContent: "space-between",
              }}
            >
              <FormControlLabel
                sx={{
                  m: 0,
                  color: "#334155",
                  "& .MuiFormControlLabel-label": {
                    fontSize: "0.84rem",
                    fontWeight: 700,
                  },
                }}
                control={
                  <Checkbox
                    checked={rememberMe}
                    onChange={(event) => setRememberMe(event.target.checked)}
                    disabled={loading}
                    size="small"
                  />
                }
                label="Se souvenir de moi"
              />

              <Typography
                component="a"
                href={SUPPORT_MAILTO}
                sx={{
                  color: "#2563eb",
                  fontSize: "0.82rem",
                  fontWeight: 850,
                  textDecoration: "none",
                  whiteSpace: "nowrap",
                  "&:hover": { textDecoration: "underline" },
                }}
              >
                Mot de passe oublié ?
              </Typography>
            </Stack>

            <Button
              type="submit"
              variant="contained"
              fullWidth
              size="large"
              disabled={loading}
              sx={{
                minHeight: 54,
                borderRadius: 2.7,
                color: "#ffffff",
                background: "linear-gradient(90deg, #0b1739 0%, #1d4ed8 58%, #2563eb 100%)",
                boxShadow: "0 12px 26px rgba(37,99,235,0.22)",
                fontWeight: 950,
                fontSize: "0.94rem",
                textTransform: "none",
                "&:hover": {
                  background: "linear-gradient(90deg, #0f172a 0%, #1e40af 58%, #1d4ed8 100%)",
                  boxShadow: "0 15px 32px rgba(37,99,235,0.28)",
                },
              }}
            >
              {loading ? "Connexion en cours..." : "Se connecter"}
            </Button>
          </Box>

          <Divider sx={{ my: 1.9, borderColor: "rgba(148,163,184,0.22)" }} />

          <Stack spacing={0.35} alignItems="center" sx={{ textAlign: "center" }}>
            <Typography
              variant="caption"
              sx={{ color: "#64748b", fontSize: "0.72rem" }}
            >
              Accès sécurisé réservé aux utilisateurs autorisés.
            </Typography>

            <Typography
              variant="caption"
              sx={{ color: "#334155", fontSize: "0.71rem", fontWeight: 850 }}
            >
              {APP_COPYRIGHT}
            </Typography>

            {APP_FOOTER_NOTE ? (
              <Typography
                variant="caption"
                sx={{
                  maxWidth: 330,
                  color: "#64748b",
                  fontSize: "0.69rem",
                  lineHeight: 1.4,
                }}
              >
                {APP_FOOTER_NOTE}
              </Typography>
            ) : null}
          </Stack>
        </CardContent>
      </Card>
    </Box>
  )
}

export default LoginPage
